<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'rental',
    path: '/rentals/:id',
    component: 'Rental',
    params: [
      {
        name: 'id',
        prop: 'rentalId',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Rental',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    rentalId: {
      type: String,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const tabManager = ref(null);
  const item = ref(null);
  const messages = ref({
    delete: $fn.value.pgettext('Rental|Header', 'Delete rental'),
  });
  const ux = ref({
    initialLoadSpinner: true,
  });
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_8564662037462133_parentTo = computed(() => ({
    name: 'app.rentals',
  }));

  const $data = computed(() => ({
    tabManager: tabManager.value,
    item: item.value,
    messages: messages.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_parentTo: b_8564662037462133_parentTo.value,
  }));

  const $$load_tabManager = async () => {
    return new $app.value.utils.LazyTabManager();
  };
  const $$load_item = async () => {
    return $fn.value.fetch('read_rental', {
      asResource: true,
      parameters: { id: props.rentalId },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'tabManager' },
      { name: 'item' },
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_parentTo', nodeId: '8564662037462133' },
    ],
    loaders: { tabManager: $$load_tabManager, item: $$load_item },
    ux: { initialLoadSpinner: true },
    dataRefs: {
      tabManager,
      item,
      messages,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_parentTo,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title: [item.value.snapshot('name'), $fn.value.gettext('Rental')],
        icon: $config.value.rental.icon,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_xapuzsmv6_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_xapuzsmv6 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_xapuzsmv6_getActionArgs,
    });
  const inst_evh_7315092382398562_close_xqpa7dh3t =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_xapuzsmv6,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_xqpa7dh3t],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_ackfsrobg =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_odiltsin5 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ackfsrobg,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_odiltsin5],
    event: 'reload',
  });
  const act_4615685607825208_reloadComponentData_kj226zgfc =
    new actionTypes.ReloadComponentDataAction({
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop_ooaaahxdz =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_reloadComponentData_kj226zgfc,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4615685607825208_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4615685607825208_clickPreventStop_ooaaahxdz],
      event: 'click.prevent.stop',
    });
  const act_2095089031469157_closeComponent_9hy1cqftn =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2095089031469157_success_ub00q881r =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_closeComponent_9hy1cqftn,
      event: 'success',
      displayName: 'closeComponent',
    });
  const inst_evh_2095089031469157_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_success_ub00q881r],
    event: 'success',
  });
  const act_2679161326024595_reloadComponentData_ydf6lneyy =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2679161326024595_reload_l0vpwyb4j =
    new actionTypes.EventHandlerImpl({
      action: act_2679161326024595_reloadComponentData_ydf6lneyy,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_2679161326024595_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2679161326024595_reload_l0vpwyb4j],
    event: 'reload',
  });
  const act_7987652045662005_reloadComponentData_hri9z1l0o =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7987652045662005_reload_e7gy8gy7a =
    new actionTypes.EventHandlerImpl({
      action: act_7987652045662005_reloadComponentData_hri9z1l0o,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7987652045662005_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7987652045662005_reload_e7gy8gy7a],
    event: 'reload',
  });
  const act_6490121676898844_reloadComponentData_sus4sd3qg =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6490121676898844_reload_er5qi1dwu =
    new actionTypes.EventHandlerImpl({
      action: act_6490121676898844_reloadComponentData_sus4sd3qg,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6490121676898844_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6490121676898844_reload_er5qi1dwu],
    event: 'reload',
  });
  const act_8697745105058001_reloadComponentData_e2rtexsnh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8697745105058001_reload_wmm2fc2sm =
    new actionTypes.EventHandlerImpl({
      action: act_8697745105058001_reloadComponentData_e2rtexsnh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_8697745105058001_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8697745105058001_reload_wmm2fc2sm],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_xqpa7dh3t,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_odiltsin5,
    inst_evh_7315092382398562_reload,
    inst_evh_4615685607825208_clickPreventStop_ooaaahxdz,
    reload: inst_evh_4615685607825208_clickPreventStop_ooaaahxdz,
    inst_evh_4615685607825208_clickPreventStop,
    inst_evh_2095089031469157_success_ub00q881r,
    inst_evh_2095089031469157_success,
    inst_evh_2679161326024595_reload_l0vpwyb4j,
    inst_evh_2679161326024595_reload,
    inst_evh_7987652045662005_reload_e7gy8gy7a,
    inst_evh_7987652045662005_reload,
    inst_evh_6490121676898844_reload_er5qi1dwu,
    inst_evh_6490121676898844_reload,
    inst_evh_8697745105058001_reload_wmm2fc2sm,
    inst_evh_8697745105058001_reload,
  };

  const evh_7315092382398562_close_xqpa7dh3t = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_xqpa7dh3t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_odiltsin5 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_odiltsin5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop_ooaaahxdz = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop_ooaaahxdz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success_ub00q881r = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success_ub00q881r.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_success = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload_l0vpwyb4j = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload_l0vpwyb4j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2679161326024595_reload = async (event, scope) => {
    return await actions.inst_evh_2679161326024595_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload_e7gy8gy7a = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload_e7gy8gy7a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7987652045662005_reload = async (event, scope) => {
    return await actions.inst_evh_7987652045662005_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload_er5qi1dwu = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload_er5qi1dwu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6490121676898844_reload = async (event, scope) => {
    return await actions.inst_evh_6490121676898844_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_reload_wmm2fc2sm = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_reload_wmm2fc2sm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8697745105058001_reload = async (event, scope) => {
    return await actions.inst_evh_8697745105058001_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Rental"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="page--v2 d-flex flex-column" v-if="loaded">
      <MaterialTabs
        :tabManager="tabManager"
        :title="item.snapshot('name')"
        :parentText="gettext('Rentals')"
        :parentTo="b_8564662037462133_parentTo"
        class="h-100"
      >
        <template #actions>
          <ItpButton
            variant="light"
            icon="fa-sync-alt"
            :text="gettext('Reload')"
            size="sm"
            :spinning="actions.reload.isRunning"
            @click.prevent.stop="
              evh_4615685607825208_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <BtnDeleteResource
            :resource="item"
            :dialogHeader="messages.delete"
            @success="evh_2095089031469157_success($event, {})"
          >
          </BtnDeleteResource>
        </template>
        <BTab :title="gettext('General')" class="container float-start">
          <ItpRow v-if="tabManager.visited(0)">
            <ItpCol :w="6">
              <UcRentalGeneralPanel
                :rental="item"
                :reload="lastLoaded"
                class="pb-5"
                @reload="evh_2679161326024595_reload($event, {})"
              >
              </UcRentalGeneralPanel>
              <UcRentalFormContracts
                :rental="item"
                :reload="lastLoaded"
                class="pb-5"
                v-if="item"
                @reload="evh_7987652045662005_reload($event, {})"
              >
              </UcRentalFormContracts>
              <UcRentalFormProperties
                :rentalId="item.id"
                :reload="lastLoaded"
                class="pb-5"
                v-if="item"
                @reload="evh_6490121676898844_reload($event, {})"
              >
              </UcRentalFormProperties>
            </ItpCol>
            <ItpCol :w="6">
              <UcCommentsList :parent="item" :reload="lastLoaded" class="pb-5">
              </UcCommentsList>
              <UcAttachmentsList
                :parent="item"
                :reload="lastLoaded"
                class="pb-5"
              >
              </UcAttachmentsList>
            </ItpCol>
          </ItpRow>
        </BTab>
        <BTab :title="gettext('Services')" class="container-fluid h-100">
          <UcRentalServices
            :rental="item"
            :reload="lastLoaded"
            v-if="tabManager.visited(1)"
            @reload="evh_8697745105058001_reload($event, {})"
          >
          </UcRentalServices>
        </BTab>
        <BTab :title="gettext('Fixed Assets')" class="container-fluid h-100">
          <UcRentalFormFixedAssets
            :rentalId="item.id"
            :editable="!item.locked"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(2)"
          >
          </UcRentalFormFixedAssets>
        </BTab>
        <BTab
          :title="pgettext('Rental', 'Billing list')"
          class="container-fluid h-100"
        >
          <UcBillingList
            :rentalId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(3)"
          >
          </UcBillingList>
        </BTab>
        <BTab
          :title="pgettext('Rental', 'Invoices')"
          class="container-fluid h-100"
        >
          <InvoiceLinesView
            :rentalId="item.id"
            :reload="lastLoaded"
            class="h-100"
            v-if="tabManager.visited(4)"
          >
          </InvoiceLinesView>
        </BTab>
      </MaterialTabs>
    </div>
  </ItpPage>
</template>
